import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  postSuggestedDataAccepted,
  postSuggestedDataRejected,
  postSuggestedDataShown,
} from 'src/utils/growthEngineSuggestedData'
import { omitInternalOrFixtureSuggestedItems } from 'src/utils/growthEngineInternalTitle'

const itemIdentity = item =>
  item?.productId ||
  item?.purchaseOrderNumber ||
  item?.venueSupplierId ||
  item?.supplierId ||
  item?.title ||
  ''

/**
 * Suggested-data chips for Guided Success empty states.
 * Reject hides chips locally; the primary CTA stays for a blank first try.
 */
export default function useGrowthEngineSuggestedData({
  venueId,
  featureId,
  items,
} = {}) {
  const [rejected, setRejected] = useState(false)
  const shownKeyRef = useRef('')
  const sourceItems = omitInternalOrFixtureSuggestedItems(
    Array.isArray(items) ? items : [],
  )
  const itemKey = sourceItems.map(itemIdentity).join('|')

  useEffect(() => {
    setRejected(false)
    shownKeyRef.current = ''
  }, [venueId, featureId])

  const visibleItems = useMemo(
    () => (rejected ? [] : sourceItems),
    [rejected, sourceItems],
  )

  useEffect(() => {
    if (!venueId || !featureId || rejected || !sourceItems.length)
      return undefined
    const key = `${venueId}:${featureId}:${itemKey}`
    if (shownKeyRef.current === key) return undefined
    shownKeyRef.current = key
    postSuggestedDataShown(venueId, featureId, sourceItems).catch(err => {
      console.warn('Growth Engine suggested_data_shown failed', err)
    })
    return undefined
  }, [venueId, featureId, rejected, itemKey, sourceItems])

  const acceptItem = useCallback(
    (item, { edit = false } = {}) => {
      postSuggestedDataAccepted(venueId, featureId, item, { edit }).catch(
        err => {
          console.warn('Growth Engine suggested_data_accepted failed', err)
        },
      )
      return item
    },
    [venueId, featureId],
  )

  const rejectItems = useCallback(() => {
    setRejected(true)
    postSuggestedDataRejected(venueId, featureId).catch(err => {
      console.warn('Growth Engine suggested_data_rejected failed', err)
    })
  }, [venueId, featureId])

  return { visibleItems, rejected, acceptItem, rejectItems }
}
